import React, { useState } from 'react';

import classNames from 'classnames';

import { decimal, decimalUpTo } from 'Helpers/currencyFormat';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

const DASH = '–';

/** A figure at the report's unit; null is a dash. Mirrors the management pack's rounding. */
export const formatFigure = (value, figures) => {
    if (value === null || value === undefined) {
        return DASH;
    }

    const decimals = figures.unit === 'whole' ? 0 : figures.decimals;
    const scaled = Number(value) / figures.divisor;
    const factor = 10 ** decimals;
    const rounded = (Math.sign(scaled) * Math.round(Math.abs(scaled) * factor)) / factor;
    const text = decimal(rounded, decimals);

    return /[1-9]/.test(text) ? text : text.replace(/^[-−]/, '');
};

const cellClass = 'tw-border-b tw-border-grey tw-px-3 tw-py-2.5 tw-align-middle';
const numberClass = classNames(cellClass, 'tw-text-end tw-whitespace-nowrap');
const headClass = 'tw-border-b tw-border-grey tw-px-3 tw-py-2.5 tw-text-xs tw-font-medium tw-text-charcoal-75 tw-whitespace-nowrap';

function FigureCells({ row, compared, figures, withLines }) {
    const keys = ['budget', ...(compared ? ['compare_budget', 'variance'] : []), 'committed', 'uncommitted', 'actuals'];

    return (
        <>
            <td className={numberClass}>{withLines ? row.lines : ''}</td>
            {keys.map((key) => (
                <td key={key} className={numberClass}>
                    {formatFigure(row[key], figures)}
                </td>
            ))}
        </>
    );
}

function LineRow({ line, compared, figures }) {
    return (
        <tr className="tw-text-charcoal">
            <td className={classNames(cellClass, 'tw-ps-12')}>
                {line.label}
                {line.percentage !== null && line.percentage < 100 && (
                    <span className="tw-ms-1.5 tw-text-2xs tw-text-charcoal-50">{`${decimalUpTo(line.percentage, 2)}%`}</span>
                )}
            </td>
            <FigureCells row={line} compared={compared} figures={figures} withLines={false} />
        </tr>
    );
}

function DepartmentRows({ department, compared, figures }) {
    return (
        <>
            <tr className="tw-font-semibold tw-text-charcoal">
                <td className={classNames(cellClass, 'tw-ps-7')}>{department.name}</td>
                <FigureCells row={department} compared={compared} figures={figures} withLines />
            </tr>
            {department.rows.map((line) => (
                <LineRow key={line.key} line={line} compared={compared} figures={figures} />
            ))}
        </>
    );
}

function BucketRows({ bucket, compared, figures }) {
    const [open, setOpen] = useState(false);
    const expandable = bucket.departments.length > 0;
    const isUntagged = bucket.kind === 'untagged';
    const Caret = open ? ChevronDownIcon : ChevronRightIcon;

    return (
        <>
            <tr className={classNames('tw-bg-charcoal-10', isUntagged ? 'tw-font-medium tw-text-charcoal-75' : 'tw-font-semibold tw-text-charcoal')}>
                <td className={cellClass}>
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        {expandable ? (
                            <button
                                type="button"
                                className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-start"
                                aria-expanded={open}
                                onClick={() => setOpen((value) => !value)}
                            >
                                <Caret className="tw-h-3 tw-w-3 tw-flex-shrink-0" aria-hidden="true" />
                                <span>{bucket.label}</span>
                            </button>
                        ) : (
                            <span className="tw-ps-[1.125rem]">{bucket.label}</span>
                        )}
                        {bucket.url && (
                            <a href={bucket.url} className="tw-text-xs tw-font-semibold tw-text-blue hover:tw-underline">
                                {$t('open tag view')}
                            </a>
                        )}
                    </div>
                </td>
                <FigureCells row={bucket} compared={compared} figures={figures} withLines />
            </tr>
            {open &&
                bucket.departments.map((department) => <DepartmentRows key={department.key} department={department} compared={compared} figures={figures} />)}
        </>
    );
}

/**
 * The report table: one shaded row per tag, Untagged and Other tags, each expanding to
 * its departments and lines as far as the run's detail allows, then Total expenditure.
 */
export default function BudgetsByTagTable({ data }) {
    const compared = data.compare !== null;
    const buckets = data.buckets.filter((bucket) => bucket.kind !== 'total');
    const total = data.buckets.find((bucket) => bucket.kind === 'total');
    const columns = [
        $t('Budget'),
        ...(compared ? [$t('Budget ({{version}})', { version: data.compare.label }), $t('Variance')] : []),
        $t('Committed'),
        $t('Uncommitted'),
        $t('Actuals to date'),
    ];

    return (
        <div className="tw-overflow-x-auto tw-rounded-lg tw-border-2 tw-border-grey">
            <table className="tw-w-full tw-min-w-[900px] tw-border-separate tw-border-spacing-0 tw-text-sm">
                <thead>
                    <tr>
                        <th className={classNames(headClass, 'tw-text-start')}>{$t('Tag')}</th>
                        <th className={classNames(headClass, 'tw-text-end tw-w-20')}>{$t('Lines')}</th>
                        {columns.map((column) => (
                            <th key={column} className={classNames(headClass, 'tw-text-end tw-w-32')}>
                                {column}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {buckets.map((bucket) => (
                        <BucketRows key={bucket.key} bucket={bucket} compared={compared} figures={data.figures} />
                    ))}
                </tbody>
                {total && (
                    <tfoot>
                        <tr className="tw-font-semibold tw-text-charcoal">
                            <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5">{total.label}</td>
                            <td className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5 tw-text-end tw-whitespace-nowrap">{total.lines}</td>
                            {['budget', ...(compared ? ['compare_budget', 'variance'] : []), 'committed', 'uncommitted', 'actuals'].map((key) => (
                                <td key={key} className="tw-border-t-2 tw-border-charcoal-25 tw-px-3 tw-py-2.5 tw-text-end tw-whitespace-nowrap">
                                    {formatFigure(total[key], data.figures)}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
